var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Spacer, { ESpacerWeight, } from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import "draft-js/dist/Draft.css";
import React from "react";
import { DisplayText } from "../../../../../../components/DisplayText";
import { Modal } from "../../../../../../components/Modal";
import { MultiStepForm } from "../../../../../../components/SharedForm/MultiStepForm";
import { TextStyle } from "../../../../../../components/TextStyle";
import { getKeysForConditionOrLoop, performActions, removeBlocks, removeEntities, } from "../../../../utils/draftjs.utils";
import schemas from "../../../InsertConditionModal/schemas";
import { getCleanFormData } from "../../../InsertConditionModal/schemas/schema.insertCondition.utils";
import { DisplayTypeContext } from "../../contexts/DisplayType.context";
import { EditorStateContext } from "../../contexts/EditorState.context";
import { FieldsContext } from "../../contexts/Fields.context";
import { CONDITION_RENDERER_TYPE } from "../../decorators/ConditionRenderer";
import { DEFAULT_VARIABLES } from "../../Editor.config";
import ConditionInfo from "../ConditionInfo";
import * as S from "./ConditionModal.styles";
var ConditionModal = function (props) {
    var _a;
    var id = props.id, onClose = props.onClose;
    // Context
    var isTemplate = React.useContext(DisplayTypeContext).isTemplate;
    var _b = React.useContext(FieldsContext), fields = _b.fields, setFields = _b.setFields, removeCondition = _b.removeCondition;
    var _c = React.useContext(EditorStateContext), editorState = _c.editorState, setEditorState = _c.setEditorState;
    var condition = fields.conditions.find(function (x) { return x.id === id; });
    var variableId = condition === null || condition === void 0 ? void 0 : condition.variableId;
    var variable = (_a = fields.variables) === null || _a === void 0 ? void 0 : _a.find(function (x) { return x.id === variableId; });
    // Refs
    var editFormRef = React.createRef();
    // State
    var formId = React.useState(generateFormId())[0];
    var _d = React.useState(0), tabIndex = _d[0], setTabIndex = _d[1];
    // Helpers
    var insertFields = function (formData) {
        if (!condition) {
            return;
        }
        var _a = getCleanFormData(formData), variableId = _a.variableId, newConditionConfig = __rest(_a, ["variableId"]);
        var existingVariable = __spreadArray(__spreadArray([], fields.variables, true), DEFAULT_VARIABLES, true).find(function (x) { return x.id === variableId; });
        var newCondition = __assign({ id: condition.id, variableId: variableId }, newConditionConfig);
        setFields({
            groupId: existingVariable === null || existingVariable === void 0 ? void 0 : existingVariable.groupId,
            variableId: variableId,
            condition: newCondition,
        });
        return newCondition;
    };
    // Event handlers
    var onUpdate = function (formData) {
        if (!condition) {
            showAlert({
                type: "error",
                message: getLocalizedText("No condition"),
            });
            return;
        }
        insertFields(formData);
        showAlert({
            type: "info",
            message: getLocalizedText("Condition updated"),
        });
        onClose();
    };
    var handleRemoveInstance = function () {
        if (!editorState || !condition) {
            return;
        }
        var _a = getKeysForConditionOrLoop({
            editorState: editorState,
            rendererType: CONDITION_RENDERER_TYPE,
            id: id,
        }), entityKeys = _a.entityKeys, blockKeys = _a.blockKeys;
        setEditorState(performActions(editorState, [
            function (x) { return removeEntities({ editorState: x, entityKeys: entityKeys }); },
            function (x) { return removeBlocks({ editorState: x, blockKeys: blockKeys }); },
        ]));
        removeCondition(id);
        onClose();
        showAlert({
            type: "info",
            message: getLocalizedText("Condition removed"),
        });
    };
    var handleRemoveInstanceWithConfirm = function () {
        confirm({
            title: getLocalizedText("editor.condition_instance.remove.confirm.title"),
            primaryActions: [
                {
                    title: getLocalizedText("system.remove"),
                    onPress: handleRemoveInstance,
                },
            ],
        });
    };
    // Render
    if (!condition || !variable) {
        return (_jsx(Modal, { onClose: onClose, heading: "Condition", shouldCloseOnOverlayClick: true, hasDismiss: true, width: "medium", children: _jsx(DisplayText, { size: "small", children: _jsx(TextStyle, { variation: "subdued", children: "Condition/variable not found" }) }) }));
    }
    var info = (_jsxs(_Fragment, { children: [_jsx(ConditionInfo, { condition: condition, variable: variable }), isTemplate && (_jsxs(_Fragment, { children: [_jsx(Spacer, { weight: ESpacerWeight.W32 }), _jsx(S.RemoveButton, { title: getLocalizedText("system.remove"), onPress: handleRemoveInstanceWithConfirm })] }))] }));
    var edit = (_jsx(MultiStepForm, { formId: "edit-condition-".concat(formId), schemas: schemas({
            fields: fields,
            condition: condition,
        }), withAside: false, onSuccess: onUpdate, showControls: false, submitLabel: getLocalizedText("system.update"), ref: editFormRef }));
    return (_jsx(Modal, { onClose: onClose, heading: getLocalizedText("editor.insert_custom.insert_condition"), shouldCloseOnOverlayClick: true, hasDismiss: true, width: "medium", onChangeTab: setTabIndex, tabs: [
            {
                content: getLocalizedText("system.info"),
                component: info,
                isShown: true,
            },
            {
                content: getLocalizedText("system.edit"),
                component: edit,
                isShown: isTemplate,
            },
        ].filter(function (x) { return x.isShown; }), actions: tabIndex > 0
            ? [
                {
                    content: getLocalizedText("system.update"),
                    onClick: function () {
                        var _a;
                        (_a = editFormRef.current) === null || _a === void 0 ? void 0 : _a.handleExternalSubmit();
                    },
                    appearance: "primary",
                },
            ]
            : undefined }));
};
// eslint-disable-next-line import/no-default-export
export default ConditionModal;
